import { JPBReferenceProperty, PriceMode, AreaUnit, DEFAULT_ASSUMPTIONS, YearGrouping } from "./JPBState";
import { calculateEffectiveMarkSquareMeterPrice, formatDate, formatPricePerSquareMeter } from "./JPBFunc";

import JPBRegions from "./JPBRegions.json";
import JPBIndexData from "./JPBIndexOfLandPrices.json";
import { SettingsManager } from "@viamap/viamap2-common/dist/managers/SettingsManager";
import { ESInterfaceJWTAuth } from "src/compentsModus/ESInterfaceJWTAuth";

export type JPBReferenceTransactionElement = {
   geometry: any;
   centroid: any;
   date: Date;
   pricePerSquareMeter: number;
   areaInSquareMeter: number;
   totalPrice: number;
   transactionType: string;
   priceTodayPerSquareMeter: number;
   priceTodayTotal: number;
}

export type JPBQueryOptions = {
   dateFrom?: string;
   landbrugsnotering?: string;
   polygonCoordinates?: number[][][];
   size?: number;
   yearGrouping?: YearGrouping;
}

export type JPBDatabaseMetadata = {
   dataset: string;
   targetIndex: string;
   uploadBatchId: string;
   uploadedDocs: number;
   uploadedAt: number;
   esDocCountAfter: number;
}

export class JPBManager {
   private static ESInterfaceJWTAuth: ESInterfaceJWTAuth | null = null;
   private static currentIndexName: string | null = null;
   private static databaseMetadata: JPBDatabaseMetadata | null = null;
   private static metadataLastFetched: number | null = null;

   private static getESInterfaceJWTAuth(): ESInterfaceJWTAuth {
      const indexName = SettingsManager.getSystemSetting("jpb.indexName", "jpb_landbrugshandler_testv5");

      // Recreate if index name changed or doesn't exist
      if (!JPBManager.ESInterfaceJWTAuth || JPBManager.currentIndexName !== indexName) {
         JPBManager.ESInterfaceJWTAuth = new ESInterfaceJWTAuth(indexName);
         JPBManager.currentIndexName = indexName;
      }
      return JPBManager.ESInterfaceJWTAuth;
   }

   public static async fetchDatabaseMetadata(forceRefresh: boolean = false): Promise<JPBDatabaseMetadata | null> {
      // Cache for 5 minutes
      const cacheTimeout = 5 * 60 * 1000; // 5 minutes in milliseconds
      const now = Date.now();

      if (!forceRefresh && JPBManager.databaseMetadata && JPBManager.metadataLastFetched && (now - JPBManager.metadataLastFetched) < cacheTimeout) {
         return JPBManager.databaseMetadata;
      }

      try {
         const indexName = SettingsManager.getSystemSetting("jpb.indexName", "jpb_landbrugshandler_testv5");
         const metaIndexName = `${indexName}__meta`;
         const metaESInterface = new ESInterfaceJWTAuth(metaIndexName);

         // Use query instead of getDocument to handle stored fields and encoding issues
         const queryBody = {
            query: {
               ids: {
                  values: ["skødeArealer_latest"]
               }
            },
            stored_fields: ["*"],
            _source: true
         };

         const response = await metaESInterface.doQuery(queryBody);

         const hits = response.hits?.hits || [];
         if (hits.length === 0) {
            return null;
         }

         const hit = hits[0];
         const source = hit._source || {};
         const fields = hit.fields || {};

         // Prefer fields if available, otherwise use _source
         const dataset = fields.dataset?.[0] || fields["dataset.keyword"]?.[0] || source.dataset || "";
         const targetIndex = fields.targetIndex?.[0] || fields["targetIndex.keyword"]?.[0] || source.targetIndex || "";
         const uploadBatchId = fields.uploadBatchId?.[0] || source.uploadBatchId || "";
         const uploadedDocs = fields.uploadedDocs?.[0] || source.uploadedDocs || 0;
         const uploadedAt = fields.uploadedAt?.[0] || source.uploadedAt || 0;
         const esDocCountAfter = fields.esDocCountAfter?.[0] || source.esDocCountAfter || 0;

         JPBManager.databaseMetadata = {
            dataset,
            targetIndex,
            uploadBatchId,
            uploadedDocs,
            uploadedAt,
            esDocCountAfter
         };

         JPBManager.metadataLastFetched = now;
         return JPBManager.databaseMetadata;
      } catch (err: any) {
         return null;
      }
   }

   public static getDatabaseMetadata(): JPBDatabaseMetadata | null {
      return JPBManager.databaseMetadata;
   }

   private static async fetchAllDataWithPagination(
      indexName: string,
      baseQuery: any,
      maxRows: number = 1000,
      onProgress?: (progress: { totalFetched: number; total: number; currentPage: number; sizeMB: number }) => void
   ): Promise<any> {
      const allHits: any[] = [];
      let currentPage = 0;
      let total: number | null = null;
      let searchAfter: any[] | null = null;
      let hasMore = true;
      const maxPages = 10000; // Safety limit

      // Ensure query has sort for search_after
      // Note: Can't use _id for sorting (fielddata disabled), so we use dato only
      // If dato has duplicates, we'll use _seq_no as tiebreaker (available in ES responses)
      const queryWithSort = baseQuery.sort
         ? baseQuery
         : { ...baseQuery, sort: [{ dato: 'desc' }, { _seq_no: 'desc' }] };

      // Get noSQLDataUrl and JWT token
      const noSQLDataUrl = SettingsManager.getSystemSetting("getNoSQLDataUrl");

      // Get JWT token (similar to ESInterfaceJWTAuth)
      const getJwtToken = (): string | undefined => {
         try {
            const authData = SettingsManager.getSystemSetting("authData");
            if (!authData || !authData.ClientId) {
               return undefined;
            }
            const lastAuthUserKey = `CognitoIdentityServiceProvider.${authData.ClientId}.LastAuthUser`;
            const username = localStorage.getItem(lastAuthUserKey);
            if (!username) {
               return undefined;
            }
            const idTokenKey = `CognitoIdentityServiceProvider.${authData.ClientId}.${username}.idToken`;
            return localStorage.getItem(idTokenKey) || undefined;
         } catch (error) {
            return undefined;
         }
      };

      while (hasMore && currentPage < maxPages) {
         currentPage++;

         try {
            // Build Elasticsearch query
            const esQuery: any = {
               ...queryWithSort,
               size: maxRows
            };

            if (searchAfter) {
               esQuery.search_after = searchAfter;
            }

            // Call Lambda with pagination parameters (format expected by Lambda)
            const requestBody = {
               index: `/${indexName}/_search`,
               query: esQuery,
               maxRows: maxRows,
               ...(searchAfter && { searchAfter: searchAfter })
            };

            const jwtToken = getJwtToken();

            const response = await fetch(noSQLDataUrl, {
               method: "POST",
               headers: {
                  "content-type": "application/json",
                  ...(jwtToken && { "Authorization": `Bearer ${jwtToken}` })
               },
               body: JSON.stringify(requestBody)
            });

            if (response.status !== 200) {
               const errorText = await response.text();
               throw new Error(`HTTP ${response.status}: ${errorText}`);
            }

            const body = await response.json();

            // Handle pagination metadata from Lambda response
            const pagination = body.pagination;
            const hits = body.hits?.hits || [];
            allHits.push(...hits);

            // Store total from first response
            if (currentPage === 1) {
               const totalObj = body.hits?.total;
               total = typeof totalObj === 'object' ? totalObj.value : totalObj;
            } else if (pagination) {
               total = pagination.total;
            }

            // Update pagination state
            if (pagination) {
               hasMore = pagination.hasMore;
               searchAfter = pagination.nextSearchAfter;
            } else {
               // Fallback: check if we got fewer results than requested
               hasMore = hits.length === maxRows;
               if (hasMore && hits.length > 0) {
                  const lastHit = hits[hits.length - 1];
                  // Use sort array from hit, or construct from dato and _seq_no
                  if (lastHit.sort) {
                     searchAfter = lastHit.sort;
                  } else if (lastHit._source?.dato !== undefined && lastHit._seq_no !== undefined) {
                     searchAfter = [lastHit._source.dato, lastHit._seq_no];
                  } else {
                     // Last resort: just use dato if available
                     searchAfter = lastHit._source?.dato !== undefined ? [lastHit._source.dato] : null;
                  }
               }
            }

            // Progress callback
            if (onProgress) {
               // Calculate cumulative size in MB
               const cumulativeSizeBytes = new Blob([JSON.stringify({ hits: { hits: allHits } })]).size;
               const cumulativeSizeMB = cumulativeSizeBytes / (1024 * 1024);

               onProgress({
                  totalFetched: allHits.length,
                  total: total || allHits.length,
                  currentPage,
                  sizeMB: cumulativeSizeMB
               });
            }

            // Safety check: if we got no results, we're done
            if (hits.length === 0) {
               hasMore = false;
            }

         } catch (error: any) {
            throw new Error(`Failed to fetch page ${currentPage}: ${error.message || error}`);
         }
      }

      return {
         hits: allHits,
         total: total || allHits.length
      };
   }

   public static async fetchReferenceProperties(
      options: JPBQueryOptions = {},
      _abortSignal?: AbortSignal
   ): Promise<JPBReferenceProperty[]> {
      const {
         polygonCoordinates = [
            [
               [11.835630900078542, 55.60278752391599],
               [12.223742781132785, 55.53421053360438],
               [11.25270784583796, 55.35645062377134],
               [11.835630900078542, 55.60278752391599]
            ]
         ],
         size = SettingsManager.getSystemSetting("jpb.maxProperties", 4000),
         yearGrouping = YearGrouping.FiveYears
      } = options;

      const indexName = SettingsManager.getSystemSetting("jpb.indexName", "jpb_landbrugshandler_testv5");

      const metadata = await JPBManager.fetchDatabaseMetadata();

      const baseDate = metadata?.uploadedAt
         ? new Date(metadata.uploadedAt)
         : new Date();
      const dateThreshold = new Date(baseDate);
      dateThreshold.setFullYear(dateThreshold.getFullYear() - yearGrouping);
      const dateThresholdTimestamp = Math.floor(dateThreshold.getTime());


      const isMultiPolygon = polygonCoordinates && polygonCoordinates.length > 1;

      let geoShapeType: string;
      let geoShapeCoordinates: any;

      if (isMultiPolygon) {
         geoShapeType = "MultiPolygon";
         geoShapeCoordinates = polygonCoordinates.map(ring => [ring]);
      } else {
         geoShapeType = "Polygon";
         geoShapeCoordinates = polygonCoordinates;
      }

      const baseQuery = {
         query: {
            bool: {
               filter: [
                  {
                     geo_shape: {
                        geopoint: {
                           shape: {
                              type: geoShapeType,
                              coordinates: geoShapeCoordinates
                           },
                           relation: "within"
                        }
                     }
                  },
                  {
                     range: {
                        dato: {
                           gte: dateThresholdTimestamp
                        }
                     }
                  }
               ]
            }
         },
         version: true
      };

      // Use pagination to fetch all data
      const maxRowsPerPage = 50; // Small pages to handle geometries and stay under 6MB limit
      const paginationResult = await JPBManager.fetchAllDataWithPagination(
         indexName,
         baseQuery,
         maxRowsPerPage
      );

      const allHits = paginationResult.hits;
      const totalHits = paginationResult.total;



      type ESType<T> = {
         _index: string;
         _id: string;
         _score: number;
         _source: T;
      }

      type JPBESType = {
         "id": string,
         "geopoint": [number, number],
         "landbrugsnotering"?: string,
         "adresse"?: string,
         "bfenummer"?: string,
         "geoshape"?: any,
         "komkode"?: string,
         "m2total": number,
         "m2vej": number,
         "m2bygning"?: number,
         "m2skov"?: number,
         "m2landbrug"?: number,
         "jPris"?: number,
         "pris": number,
         "dato": number, // timestamp since epoch
         "type"?: string,
         "error"?: number,
         "prisDriftsbygning"?: number | null,
         "prisStuehus"?: number | null,
         "markOverlapPct"?: number | null,
         "markOverlapGeometry"?: any | null,
         "markOverlapLayerYear"?: number | null,
         "markOverlapCalculatedAt"?: number | null,
         "jordbundskortGeometry"?: any | null,
         "jordbundskortOverlap"?: { [soilType: string]: number } | null,
         "jordbundskortCalculatedAt"?: number | null,
      }

      const validProperties = allHits.map((hit: ESType<JPBESType>) => {
         const source = hit._source;

         let originalTotalPrice = source.pris || 0;
         let totalPrice = JPBManager.calculatePresentValue(originalTotalPrice, new Date(source.dato), source.komkode || "");
         let area = source.m2total || 0;
         let originalPricePerSquareMeter = area > 0 ? originalTotalPrice / area : 0;
         let adjustedPricePerSquareMeter = area > 0 ? totalPrice / area : 0;

         // Extract overlap data from ES (fields may not exist if pipeline hasn't run yet)
         const markOverlapPct = source.markOverlapPct ?? null;
         const markOverlapGeometry = source.markOverlapGeometry || null;

         // Parse jordbundskortGeometry if it's a stringified FeatureCollection
         let jordbundskortGeometry = source.jordbundskortGeometry || null;
         if (jordbundskortGeometry && typeof jordbundskortGeometry === 'string') {
            try {
               jordbundskortGeometry = JSON.parse(jordbundskortGeometry);
            } catch (e) {
               jordbundskortGeometry = null;
            }
         }

         const jordbundskortOverlap = source.jordbundskortOverlap || null;


         return {
            bfe_nr: source.bfenummer,
            documentID: hit._source.id,
            area: area,
            totalPrice: totalPrice,
            transactionDate: new Date(source.dato || ""),
            pricePerSquareMeter: adjustedPricePerSquareMeter,
            originalPricePerSquareMeter: originalPricePerSquareMeter,
            coordinates: {
               lat: source.geopoint ? source.geopoint[1] : 0,
               lng: source.geopoint ? source.geopoint[0] : 0
            },
            geoshape: source.geoshape || null,
            address: "",
            transactionType: source.type || "",
            transactionStatus: "",
            error: source.error || 0,
            prisDriftsbygning: source.prisDriftsbygning ?? null,
            prisStuehus: source.prisStuehus ?? null,
            overlapGeometry: {
               farmlandGeometry: markOverlapGeometry,
               jordbundskortGeometry: jordbundskortGeometry
            },
            overlapArea: {
               markOverlap: markOverlapPct,
               jordbundskortOverlap: jordbundskortOverlap
            }
         };
      }).filter(isValidtJPBReferenceProperty);

      return validProperties;
   }

   public static convertToGeoJSON(
      properties: JPBReferenceProperty[],
      checkedItems: Set<string>
   ): any {
      const systemAssumptions = SettingsManager.getSystemSetting("jpb.assumptions", DEFAULT_ASSUMPTIONS);
      const thresholdM2 = systemAssumptions.buildingAreaOkM2 ?? DEFAULT_ASSUMPTIONS.buildingAreaOkM2 ?? 0;
      const useAgriculturalArea =
         systemAssumptions.buildingAreaOkUseAgriculturalArea ?? DEFAULT_ASSUMPTIONS.buildingAreaOkUseAgriculturalArea ?? false;

      return {
         type: "FeatureCollection",
         features: properties.map((prop) => {
            const effectiveMarkPrice = calculateEffectiveMarkSquareMeterPrice(prop);

            const hasBuildingValue =
               (typeof prop.prisDriftsbygning === "number" && Number.isFinite(prop.prisDriftsbygning) && prop.prisDriftsbygning > 0) ||
               (typeof prop.prisStuehus === "number" && Number.isFinite(prop.prisStuehus) && prop.prisStuehus > 0);
            const markOverlapPct = prop.overlapArea?.markOverlap ?? 0;
            const buildingAreaM2 = useAgriculturalArea ? prop.area * (markOverlapPct / 100) : prop.area;
            const buildingLocked = hasBuildingValue && thresholdM2 > 0 && buildingAreaM2 < thresholdM2;

            const formattedStandardM2 = prop.pricePerSquareMeter
               ? formatPricePerSquareMeter(prop.pricePerSquareMeter, AreaUnit.SquareMeter, 2, false)
               : null;
            const formattedStandardHa = prop.pricePerSquareMeter
               ? formatPricePerSquareMeter(prop.pricePerSquareMeter, AreaUnit.Hectare, 0, false)
               : null;
            const formattedLandbrugM2 = effectiveMarkPrice
               ? formatPricePerSquareMeter(effectiveMarkPrice, AreaUnit.SquareMeter, 2, false)
               : null;
            const formattedLandbrugHa = effectiveMarkPrice
               ? formatPricePerSquareMeter(effectiveMarkPrice, AreaUnit.Hectare, 0, false)
               : null;

            const formattedOriginalM2 = prop.originalPricePerSquareMeter
               ? formatPricePerSquareMeter(prop.originalPricePerSquareMeter, AreaUnit.SquareMeter, 2, false)
               : null;
            const formattedOriginalHa = prop.originalPricePerSquareMeter
               ? formatPricePerSquareMeter(prop.originalPricePerSquareMeter, AreaUnit.Hectare, 0, false)
               : null;

            const isChecked = checkedItems.has(prop.documentID);
            let buildingIconName: string | null = null;
            if (hasBuildingValue) {
               if (buildingLocked) {
                  buildingIconName = 'jpb-house-locked';
               } else if (isChecked) {
                  buildingIconName = 'jpb-house-checked';
               } else {
                  buildingIconName = 'jpb-house-unchecked';
               }
            }

            return {
               id: prop.documentID,
               type: "Feature",
               geometry: {
                  type: "Point",
                  coordinates: [prop.coordinates.lng, prop.coordinates.lat]
               },
               properties: {
                  bfe_nr: prop.bfe_nr,
                  documentID: prop.documentID,
                  checked: isChecked,
                  pricePerSquareMeter: prop.pricePerSquareMeter,
                  originalPricePerSquareMeter: prop.originalPricePerSquareMeter,
                  effectiveMarkPrice: effectiveMarkPrice,
                  hasBuilding: hasBuildingValue,
                  buildingLocked: !!buildingLocked,
                  buildingIconName: buildingIconName,
                  buildingIconSize: hasBuildingValue ? 1.0 : null,
                  transactionDateFormatted: formatDate(prop.transactionDate),
                  formattedStandardM2: formattedStandardM2,
                  formattedStandardHa: formattedStandardHa,
                  formattedLandbrugM2: formattedLandbrugM2,
                  formattedLandbrugHa: formattedLandbrugHa,
                  formattedOriginalM2: formattedOriginalM2,
                  formattedOriginalHa: formattedOriginalHa
               }
            };
         })
      };
   }

   public static convertToGeojsonGeometry(properties: JPBReferenceProperty[], checkedItems: Set<string>): any {
      return {
         type: "FeatureCollection",
         features: properties.map((prop) => ({
            id: prop.documentID,
            type: "Feature",
            geometry: prop.geoshape,
            properties: {
               bfe_nr: prop.bfe_nr,
               documentID: prop.documentID,
               checked: checkedItems.has(prop.documentID),
               pricePerSquareMeter: prop.pricePerSquareMeter,
            }
         }))
      }
   }


   public static extractPolygonCoordinates(bufferGeometry: any): number[][][] | null {
      if (!bufferGeometry) {
         return null;
      }

      if (bufferGeometry.type === 'FeatureCollection') {
         if (bufferGeometry.features && bufferGeometry.features.length > 0) {
            return this.extractPolygonCoordinates(bufferGeometry.features[0].geometry);
         }
         return null;
      }

      if (bufferGeometry.type === 'Feature') {
         return this.extractPolygonCoordinates(bufferGeometry.geometry);
      }

      if (bufferGeometry.type === 'Polygon' && bufferGeometry.coordinates) {
         if (bufferGeometry.coordinates.length > 0 && bufferGeometry.coordinates[0]) {
            return [bufferGeometry.coordinates[0]];
         }
      }

      // Handle MultiPolygon - extract all polygons
      if (bufferGeometry.type === 'MultiPolygon' && bufferGeometry.coordinates) {
         const polygons: number[][][] = [];
         bufferGeometry.coordinates.forEach((polygon: number[][][]) => {
            if (polygon && polygon.length > 0 && polygon[0]) {
               polygons.push(polygon[0]);
            }
         });
         return polygons.length > 0 ? polygons : null;
      }

      return null;
   }




   public static calculatePresentValue(price: number, saleDate: Date, kommune: string, compareDate?: Date) {
      function parseQuarterKey(dateStr: Date): `${number}K${number}` | null {
         if (!dateStr || isNaN(dateStr.getTime())) {
            return null;
         }
         return `${dateStr.getFullYear()}K${Math.floor(dateStr.getMonth() / 3) + 1}`;
      }

      const purchaseKey = parseQuarterKey(saleDate);
      if (!purchaseKey) return price;

      const referenceDate = compareDate || new Date();
      const referenceKey = parseQuarterKey(referenceDate);
      if (!referenceKey) return price;

      const parseKomkode = parseInt(kommune);
      if (isNaN(parseKomkode)) {
         const defaultRegion = JPBIndexData.regions.find(r => r.name === "Landsdel Vest- og Sydsjælland");
         if (defaultRegion) {
            return this.applyIndexToPrice(price, purchaseKey, referenceKey, defaultRegion);
         }
         return price;
      }

      const municipality = JPBRegions.municipalities.find((mun) => mun.kommunenr === parseKomkode);
      if (!municipality) {
         const defaultRegion = JPBIndexData.regions.find(r => r.name === "Landsdel Vest- og Sydsjælland");
         if (defaultRegion) {
            return this.applyIndexToPrice(price, purchaseKey, referenceKey, defaultRegion);
         }
         return price;
      }

      const landsdelToRegionMap: { [key: string]: string } = {
         "Hovedstaden": "Landsdel Vest- og Sydsjælland",
         "Nordsjælland": "Landsdel Vest- og Sydsjælland",
         "Østsjælland": "Landsdel Vest- og Sydsjælland",
         "Vestsjælland": "Landsdel Vest- og Sydsjælland",
         "Vest- og Sydsjælland": "Landsdel Vest- og Sydsjælland",
         "Sjælland": "Landsdel Vest- og Sydsjælland",
         "Fyn": "Landsdel Fyn",
         "Sydjylland": "Landsdel Sydjylland",
         "Østjylland": "Landsdel Østjylland",
         "Vestjylland": "Landsdel Vestjylland",
         "Nordjylland": "Landsdel Nordjylland",
         "Bornholm": "Landsdel Vest- og Sydsjælland",
         "Lolland-Falster": "Landsdel Vest- og Sydsjælland"
      };

      const landsdelNrToRegionMap: { [key: number]: string } = {
         1: "Landsdel Vest- og Sydsjælland",
         2: "Landsdel Vest- og Sydsjælland",
         3: "Landsdel Vest- og Sydsjælland",
         4: "Landsdel Vest- og Sydsjælland",
         5: "Landsdel Vest- og Sydsjælland",
         6: "Landsdel Vest- og Sydsjælland",
         7: "Landsdel Fyn",
         8: "Landsdel Sydjylland",
         9: "Landsdel Østjylland",
         10: "Landsdel Vestjylland",
         11: "Landsdel Nordjylland"
      };

      let regionName: string | undefined;

      if (municipality.landsdel) {
         regionName = landsdelToRegionMap[municipality.landsdel];
      }

      if (!regionName && municipality.landsdelNr !== undefined) {
         regionName = landsdelNrToRegionMap[municipality.landsdelNr];
      }

      if (!regionName) {
         regionName = "Landsdel Vest- og Sydsjælland";
      }

      const regions = JPBIndexData.regions;
      const region = regions.find(r => r.name === regionName);
      if (!region) {
         const defaultRegion = JPBIndexData.regions.find(r => r.name === "Landsdel Vest- og Sydsjælland");
         if (defaultRegion) {
            return this.applyIndexToPrice(price, purchaseKey, referenceKey, defaultRegion);
         }
         return price;
      }

      return this.applyIndexToPrice(price, purchaseKey, referenceKey, region);
   }

   private static applyIndexToPrice(
      price: number,
      purchaseKey: string,
      referenceKey: string,
      region: { values: { [key: string]: number } }
   ): number {

      const values = region.values || {};
      const priceIndexSalesDate = values[purchaseKey as keyof typeof values];
      const priceIndexCompaDate = values[referenceKey as keyof typeof values];

      const fallbackValue = Object.values(values).at(-1) as number | undefined;
      const finalSalesIndex = priceIndexSalesDate ?? fallbackValue;
      const finalCompareIndex = priceIndexCompaDate ?? fallbackValue;

      if (!finalSalesIndex || !finalCompareIndex) return price;

      if (purchaseKey === referenceKey || finalSalesIndex === finalCompareIndex) {
         return price;
      }

      return price * (finalCompareIndex / finalSalesIndex);
   }
}

function isValidtJPBReferenceProperty(obj: { [key: string]: unknown }): obj is JPBReferenceProperty {
   return (
      obj &&
      typeof obj.documentID === 'string' &&
      typeof obj.bfe_nr === 'string' &&
      typeof obj.error === 'number' &&
      typeof obj.area === 'number' &&
      typeof obj.totalPrice === 'number' &&
      obj.transactionDate instanceof Date &&
      typeof obj.pricePerSquareMeter === 'number' &&
      typeof obj.originalPricePerSquareMeter === 'number' &&
      typeof obj.coordinates === 'object' &&
      typeof obj.address === 'string' &&
      typeof obj.transactionType === 'string' &&
      typeof obj.transactionStatus === 'string'
   )
}